/**
 * placesApi.js — public places service.
 *
 * Fetches places from the maptodo Cloud Function and normalizes them into the
 * canonical shape (see placeNormalizer.js) so list, map, and detail pages share
 * one data model.
 *
 * Wire paths stay MapTODO `/v1/locations` (backend unchanged route prefix).
 * Public list uses `?fields=list` (slim). Detail uses `getLocation/:id` when available.
 */

import { hasArchivedFlag } from './placeFlags';
import { normalizePlace, normalizePlaceList } from './placeNormalizer';

import { cloudFunctionsOrigin } from '../shared/cloudFunctions';

const MAPTODO_CF_ORIGIN = cloudFunctionsOrigin();

export const MAPTODO_PLACES_BASE = `${MAPTODO_CF_ORIGIN}/app/v1/locations`;

export const MAPTODO_ADD_PLACE_PROD_URL = `${MAPTODO_PLACES_BASE}/addLocationProd`;

const MAPTODO_PLACES_FULL_URL = `${MAPTODO_PLACES_BASE}/getAllLocations`;
const MAPTODO_PLACES_LIST_URL = `${MAPTODO_PLACES_BASE}/getAllLocations?fields=list`;
const MAPTODO_EVENT_VENUE_LIST_URL = `${MAPTODO_PLACES_LIST_URL}&includeArchived=1`;

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const SESSION_CACHE_KEY = 'm2b-public-places-list-v2';
const VENUE_SESSION_CACHE_KEY = 'm2b-event-venue-places-v1';
const BY_ID_SESSION_KEY = 'm2b-public-place-by-id-v1';

let placesCache = null;
let placesCacheAt = 0;
let placesInflight = null;

let venuePlacesCache = null;
let venuePlacesCacheAt = 0;
let venuePlacesInflight = null;

/** @type {Map<string, { place: object, at: number }>} */
const placeByIdCache = new Map();

/** Archived event-map places. Kept out of the public by-id cache. */
/** @type {Map<string, { place: object, at: number }>} */
const eventMapPlaceCache = new Map();

function isFresh(at) {
  return typeof at === 'number' && Date.now() - at < CACHE_TTL_MS;
}

function clearSessionPlacesCache() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
      sessionStorage.removeItem(VENUE_SESSION_CACHE_KEY);
      sessionStorage.removeItem(BY_ID_SESSION_KEY);
    }
  } catch {
    /* private mode / quota */
  }
}

function readSessionPlacesRecord() {
  return readSessionRecord(SESSION_CACHE_KEY);
}

function writeSessionPlacesCache(places) {
  writeSessionRecord(SESSION_CACHE_KEY, places);
}

function readSessionRecord(key) {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.places) || typeof parsed.at !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionRecord(key, places) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), places }));
  } catch {
    /* private mode / quota */
  }
}

function readByIdSessionMap() {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(BY_ID_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.entries !== 'object' || parsed.entries == null) return {};
    return parsed.entries;
  } catch {
    return {};
  }
}

function writeByIdSessionMap(entries) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(BY_ID_SESSION_KEY, JSON.stringify({ entries }));
  } catch {
    /* private mode / quota — memory hit still stands */
  }
}

function readPlaceByIdCache(id) {
  const entry = placeByIdCache.get(id);
  if (entry && isFresh(entry.at)) {
    if (hasArchivedFlag(entry.place)) {
      forgetArchivedPlaceById(id);
      return null;
    }
    return entry.place;
  }
  if (entry) placeByIdCache.delete(id);

  const stored = readByIdSessionMap()[id];
  if (!stored || !stored.place || !isFresh(stored.at)) return null;
  if (hasArchivedFlag(stored.place)) {
    forgetArchivedPlaceById(id);
    return null;
  }
  placeByIdCache.set(id, { place: stored.place, at: stored.at });
  return stored.place;
}

function forgetArchivedPlaceById(id) {
  placeByIdCache.delete(id);
  const entries = readByIdSessionMap();
  if (!entries[id]) return;
  delete entries[id];
  writeByIdSessionMap(entries);
}

function writePlaceByIdCache(id, place) {
  if (!id || !place) return;
  const at = Date.now();
  placeByIdCache.set(id, { place, at });
  const entries = readByIdSessionMap();
  entries[id] = { place, at };
  writeByIdSessionMap(entries);
}

export function invalidatePublicPlacesCache() {
  placesCache = null;
  placesCacheAt = 0;
  placesInflight = null;
  venuePlacesCache = null;
  venuePlacesCacheAt = 0;
  venuePlacesInflight = null;
  placeByIdCache.clear();
  eventMapPlaceCache.clear();
  clearSessionPlacesCache();
}

function withoutArchived(places) {
  if (!Array.isArray(places)) return [];
  return places.filter((place) => !hasArchivedFlag(place));
}

/**
 * Sync peek of memory or session list cache (no network).
 * Used for instant paint on /places before revalidate.
 * @returns {object[]|null}
 */
export function peekPublicPlacesListCache() {
  if (placesCache !== null && isFresh(placesCacheAt)) {
    placesCache = withoutArchived(placesCache);
    return placesCache;
  }
  const record = readSessionPlacesRecord();
  if (record && isFresh(record.at)) {
    placesCache = withoutArchived(record.places);
    placesCacheAt = record.at;
    return placesCache;
  }
  return null;
}

/**
 * Last list snapshot even after the 24-hour window. Used only when a refresh fails.
 * @returns {object[]|null}
 */
export function peekStalePublicPlacesListCache() {
  if (Array.isArray(placesCache) && placesCache.length) {
    const filtered = withoutArchived(placesCache);
    return filtered.length ? filtered : null;
  }
  const record = readSessionPlacesRecord();
  if (!record?.places?.length) return null;
  const filtered = withoutArchived(record.places);
  return filtered.length ? filtered : null;
}

export function normalizePlaceId(place) {
  if (place == null) return '';
  const raw = place.id ?? place._id;
  return raw != null ? String(raw) : '';
}

/** Fetch raw place records from the API (admin use — preserves backend field names). */
export async function fetchRawPlacesList() {
  const res = await fetch(MAPTODO_PLACES_FULL_URL);
  if (!res.ok) throw new Error(`Places fetch ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.locations) ? data.locations : [];
}

export async function updatePlaceProd(placeId, payload) {
  const res = await fetch(`${MAPTODO_PLACES_BASE}/updateLocationProd/${placeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await res.json();
  if (!res.ok || !result.success) {
    throw new Error(result.message || result.error || `Update failed (${res.status})`);
  }
  invalidatePublicPlacesCache();
  return result;
}

export async function deletePlaceProd(placeId) {
  const res = await fetch(`${MAPTODO_PLACES_BASE}/deleteLocationProd/${placeId}`, {
    method: 'DELETE',
  });
  const result = await res.json();
  if (!res.ok || !result.success) {
    throw new Error(result.message || result.error || `Delete failed (${res.status})`);
  }
  invalidatePublicPlacesCache();
  return result;
}

function toPublicList(rawList) {
  return withoutArchived(normalizePlaceList(rawList));
}

/**
 * Public catalog for list/map. Slim `?fields=list` when CF supports it.
 * Memory + sessionStorage for 24 hours; throws on network/HTTP failure (does not cache []).
 */
export async function fetchPublicPlacesList() {
  if (placesCache !== null && isFresh(placesCacheAt)) {
    placesCache = withoutArchived(placesCache);
    return placesCache;
  }

  const record = readSessionPlacesRecord();
  if (record && isFresh(record.at)) {
    placesCache = withoutArchived(record.places);
    placesCacheAt = record.at;
    return placesCache;
  }

  if (placesInflight) return placesInflight;

  const promise = (async () => {
    try {
      const res = await fetch(MAPTODO_PLACES_LIST_URL);
      if (!res.ok) throw new Error(`Places fetch ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.locations) ? data.locations : [];
      placesCache = toPublicList(list);
      placesCacheAt = Date.now();
      writeSessionPlacesCache(placesCache);
      return placesCache;
    } catch (e) {
      console.warn('Places API fetch failed:', e);
      throw e;
    } finally {
      placesInflight = null;
    }
  })();

  placesInflight = promise;
  return promise;
}

/**
 * Event location catalog. Same slim list as the public catalog, including archived
 * venues. Archive only hides a place from /places — events can still link it.
 * Own memory + session cache so the public list stays filtered.
 */
export async function fetchEventVenuePlacesList() {
  if (venuePlacesCache !== null && isFresh(venuePlacesCacheAt)) {
    return venuePlacesCache;
  }

  const record = readSessionRecord(VENUE_SESSION_CACHE_KEY);
  if (record && isFresh(record.at)) {
    venuePlacesCache = record.places;
    venuePlacesCacheAt = record.at;
    return venuePlacesCache;
  }

  if (venuePlacesInflight) return venuePlacesInflight;

  const promise = (async () => {
    try {
      const res = await fetch(MAPTODO_EVENT_VENUE_LIST_URL);
      if (!res.ok) throw new Error(`Places fetch ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.locations) ? data.locations : [];
      venuePlacesCache = normalizePlaceList(list);
      venuePlacesCacheAt = Date.now();
      writeSessionRecord(VENUE_SESSION_CACHE_KEY, venuePlacesCache);
      return venuePlacesCache;
    } catch (e) {
      console.warn('Event venue places fetch failed:', e);
      throw e;
    } finally {
      venuePlacesInflight = null;
    }
  })();

  venuePlacesInflight = promise;
  return promise;
}

export function findPlaceByPublicId(places, rawId) {
  if (!rawId || !Array.isArray(places)) return null;
  const want = decodeURIComponent(String(rawId).trim());
  return (
    places.find((p) => {
      const pid = normalizePlaceId(p);
      return pid !== '' && pid === want;
    }) || null
  );
}

/**
 * Full public place for detail/modal. Prefers getLocation/:id; falls back to list scan
 * when by-id is unavailable (pre-deploy) or fails. Fresh by-id memory hits skip network.
 */
export async function fetchPublicPlaceById(placeId) {
  if (!placeId) return null;
  const want = decodeURIComponent(String(placeId).trim());
  if (!want) return null;

  const cached = readPlaceByIdCache(want);
  if (cached) return cached;

  try {
    const res = await fetch(
      `${MAPTODO_PLACES_BASE}/getLocation/${encodeURIComponent(want)}`
    );
    if (res.status === 404) return null;
    if (res.ok) {
      const data = await res.json();
      const raw = data?.location;
      if (raw && typeof raw === 'object') {
        const place = normalizePlace({ ...raw, id: raw.id ?? want });
        if (place?.id && place.title && !hasArchivedFlag(place)) {
          writePlaceByIdCache(want, place);
          return place;
        }
        return null;
      }
    }
  } catch (e) {
    console.warn('Places by-id fetch failed, falling back to list:', e);
  }

  try {
    const list = await fetchPublicPlacesList();
    const fromList = findPlaceByPublicId(list, want);
    if (fromList) writePlaceByIdCache(want, fromList);
    return fromList;
  } catch (e) {
    console.warn('Places list fallback failed:', e);
    return null;
  }
}

function rememberEventMapPlace(id, place) {
  if (!id || !place) return null;
  if (hasArchivedFlag(place)) {
    eventMapPlaceCache.set(id, { place, at: Date.now() });
    return place;
  }
  writePlaceByIdCache(id, place);
  return place;
}

/**
 * Place used by the public event map. Archived venues stay off /places, but a
 * linked event still needs coordinates. Archived results are not written to the
 * public by-id cache.
 */
export async function fetchPlaceForEventMap(placeId) {
  if (!placeId) return null;
  const want = decodeURIComponent(String(placeId).trim());
  if (!want) return null;

  const publicCached = readPlaceByIdCache(want);
  if (publicCached) return publicCached;

  const eventCached = eventMapPlaceCache.get(want);
  if (eventCached && isFresh(eventCached.at)) return eventCached.place;
  if (eventCached) eventMapPlaceCache.delete(want);

  try {
    const res = await fetch(
      `${MAPTODO_PLACES_BASE}/getLocation/${encodeURIComponent(want)}`
    );
    if (res.status === 404) return null;
    if (res.ok) {
      const data = await res.json();
      const raw = data?.location;
      if (raw && typeof raw === 'object') {
        const place = normalizePlace({ ...raw, id: raw.id ?? want });
        if (place?.id && place.title) return rememberEventMapPlace(want, place);
        return null;
      }
    }
  } catch (e) {
    console.warn('Event map place fetch failed, falling back to venue list:', e);
  }

  try {
    const list = await fetchEventVenuePlacesList();
    return rememberEventMapPlace(want, findPlaceByPublicId(list, want));
  } catch (e) {
    console.warn('Event venue list fallback failed:', e);
    return null;
  }
}
